import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import SyncRounded from '@mui/icons-material/SyncRounded';

const button = tv({
  base: 'group inline-flex items-center justify-center transition-all focus:outline-none relative select-none',
  variants: {
    color: {
      basic: 'bg-[var(--primaryColor)] text-white',
      primary:
        'bg-[var(--primaryColor500)] text-white hover:bg-[var(--primaryColor600)] focus:bg-[var(--primaryColor700)]',
      success:
        'bg-[var(--sematicSuccessColor500)] text-white hover:bg-[var(--sematicSuccessColor)] focus:bg-[var(--sematicSuccessColor)]',
      cyanSuccess:
        'bg-[var(--cyanColor)] text-white hover:bg-[var(--primaryColor900)] focus:bg-[var(--primaryColor900)]',
      outline:
        'bg-[var(--textWhite)] text-[var(--opacityTextColor)] hover:bg-[var(--textBlack4)] border border-[var(--textBlack16)] hover:border-[var(--textBlack4)]',
      soft: 'bg-[var(--bgGreyColor4)] text-[var(--opacityTextColor)] hover:text-[var(--blueColor1)]',
      danger: 'bg-[var(--colorRed)] text-white hover:bg-[var(--colorRed1)] focus:bg-[var(--colorRed)]',
      dangerSoft: 'bg-[var(--bgRed50)] text-[var(--sematicErrorColor500)] hover:opacity-80',

      text: 'text-[var(--opacityTextColor)]',
      none: 'bg-transparent text-[var(--primaryColor)] hover:bg-[var(----bgGreyColor4)] hover:font-bold',
      barcode: 'bg-transparent text-[var(--primaryColor500)] ',
      warning:
        'bg-[var(--sematicWarningColor)] text-white hover:bg-[var(--sematicWarningColor)] focus:bg-[var(--sematicWarningColor)]',
      dash: 'border border-dashed border-[var(--primaryColor500)] text-[var(--primaryColor500)] bg-[color-mix(in srgb,var(--primaryColor500)_8%,white)] hover:bg-[color-mix(in srgb,var(--primaryColor500)_16%,white)] focus:bg-[color-mix(in srgb,var(--primaryColor500)_20%,white)]',
      dashWarning:
        'border border-dashed border-[var(--sematicWarningColorCus)] bg-[color-mix(in srgb,var(--sematicWarningColorCus)_10%,white)] text-[var(--sematicWarningColorCus)] hover:bg-[color-mix(in srgb,var(--sematicWarningColorCus)_20%,white)] focus:bg-[color-mix(in srgb,var(--sematicWarningColorCus)_25%,white)]',
      dashError:
        'border border-dashed border-[var(--sematicErrorColor500)] text-[var(--sematicErrorColor500)] bg-[color-mix(in srgb,var(--sematicErrorColor500)_5%,white)] hover:bg-[color-mix(in srgb,var(--sematicErrorColor500)_10%,white)] focus:bg-[color-mix(in srgb,var(--sematicErrorColor500)_15%,white)]',
    },
    size: {
      xxs: '2xl:text-[12px] 2xl:px-[6px] 2xl:py-[6px] 2xl:min-h-[22px] 2xl:leading-[18px] xl:text-[11px] xl:px-[5px] xl:py-[5px] xl:min-h-[20px] xl:leading-[16px] lg:text-[10px] lg:px-[4px] lg:py-[4px] lg:min-h-[18px] lg:leading-[14px] md:px-[3px] md:py-[3px] md:text-[10px] md:min-h-[16px] md:leading-[12px]',
      xs: '2xl:text-[14px] 2xl:px-[12px] 2xl:py-[8px] 2xl:min-h-[24px] 2xl:leading-[20px] xl:text-[14px] xl:px-[12px] xl:py-[8px] xl:min-h-[24px] xl:leading-[22px] lg:text-[14px] lg:px-[8px] lg:py-[6px] lg:min-h-[20px] lg:leading-[14px] md:px-[6px] md:py-[8px] md:text-[14px] md:min-h-[20px] md:leading-[14px]',
      sm: 'text-[14px] min-h-[32px] px-[10px]',
      md: 'min-h-[40px] 2xl:text-[16px] 2xl:px-[16px] 2xl:py-[8px] 2xl:min-h-[40px] xl:text-[14px] xl:px-[14px] xl:py-[6px] xl:min-h-[40px] lg:text-[14px] lg:px-[10px] lg:py-[5px] lg:min-h-[40px] 2xl:leading-[24px] xl:leading-[20px] lg:leading-[14px] md:px-[8px] md:py-[4px] md:text-[14px] md:min-h-[32px] p-2',
      lg: '2xl:text-[16px] 2xl:px-[16px] 2xl:py-[12px] 2xl:min-h-[48px] xl:text-[13px] xl:px-[13px] xl:py-[10px] xl:min-h-[38px] lg:text-[10px] lg:px-[10px] lg:py-[8px] lg:min-h-[30px] 2xl:leading-[28px] xl:leading-[22px] lg:leading-[18px] md:px-[8px] md:py-[6px] md:text-[12px] md:min-h-[32px]',
      xl: '2xl:text-[16px] 2xl:px-[16px] 2xl:py-[10px] 2xl:min-h-[56px] xl:text-[13px] xl:px-[13px] xl:py-[8px] xl:min-h-[45px] lg:text-[10px] lg:px-[10px] lg:py-[6px] lg:min-h-[36px] 2xl:leading-[32px] xl:leading-[26px] lg:leading-[22px] md:px-[8px] md:py-[4px] md:text-[14px] md:min-h-[40px]',
    },
    radius: {
      circle: 'rounded-full',
      rounded: 'rounded-[8px]',
      square: '',
    },
    isDisabled: {
      true: 'opacity-[0.36] cursor-not-allowed',
      false: '',
    },
    fullWidth: {
      true: 'w-full',
      false: '',
    },
  },
  defaultVariants: {
    color: 'basic',
    size: 'md',
    fullWidth: false,
    isDisabled: false,
    radius: 'square',
  },
});

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button> & {
    loading?: boolean;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
  };

export const ButtonCommon: React.FC<ButtonProps> = ({
  children,
  color,
  size,
  fullWidth,
  radius,
  loading = false,
  icon,
  className,
  isDisabled,
  iconPosition = 'left',
  ...rest
}) => {
  return (
    <button
      type='button'
      disabled={loading || isDisabled}
      className={button({
        color,
        size,
        fullWidth,
        radius,
        isDisabled: loading || isDisabled,
        className,
      })}
      {...rest}
    >
      {/* Loading spinner or left icon */}
      {loading ? (
        <SyncRounded className='animate-spin mr-2 text-[16px]' />
      ) : (
        icon && iconPosition === 'left' && <span className='mr-2'>{icon}</span>
      )}

      {children}
      {/* right icon */}
      {icon && iconPosition === 'right' && <span className='ml-2'>{icon}</span>}
    </button>
  );
};
